/* eslint-disable @nx/enforce-module-boundaries */
import { ActivatedRouteSnapshot, Route, Routes } from '@angular/router';

import { VANILLA_LAZY_ROUTES, VANILLA_SECONDARY_ROUTES } from '@frontend/loaders/routes';
import { ClientConfigProductName } from '@frontend/vanilla/core';

import {
    activateHostProductGuard,
    clientConfigAppGuard,
    hostAppMatchAppVersionGuard,
    lastKnownProductRedirectFunction,
    matchAppVersionProductLoadGuard,
    productStyleBundleGuard,
} from './guards';

export const SPORTS_BETSTATION_ROUTES: Route[] = [];
export const CORAL_DESKTOP_ROUTES: Route[] = [];
export const CORAL_MOBILE_ROUTES: Route[] = [];
export const LADBROKES_DESKTOP_ROUTES: Route[] = [];
export const LADBROKES_MOBILE_ROUTES: Route[] = [];

export enum SamePathAppVersion {
    SPORTS_WEB_APP = 'SportsWebApp',
    SPORTS_BETSTATION_APP = 'SportsBetstationApp',
    CORAL_DESKTOP_APP = 'CoralDesktopApp',
    CORAL_MOBILE_APP = 'CoralMobileApp',
    LADBROKES_DESKTOP_APP = 'LadbrokesDesktopApp',
    LADBROKES_MOBILE_APP = 'LadbrokesMobileApp',
    GAMES = 'Games',
    CASINO = 'Casino',
    SLOTS = 'Slots',
    LIVEGAMES = 'Livegames',
}

export const appRoutes: Routes = [
    {
        path: '',
        pathMatch: 'full',
        redirectTo: lastKnownProductRedirectFunction,
    },
    {
        path: '{culture}',
        children: [
            {
                path: '',
                pathMatch: 'full',
                redirectTo: lastKnownProductRedirectFunction,
            },
            {
                path: 'p',
                loadChildren: () => import('@frontend/vanilla/features/public-page').then((m) => m.ROUTES),
                data: {
                    publicPageRoot: 'MobilePortal-v1.0/PublicPages/',
                    product: ClientConfigProductName.PORTAL,
                },
            },
            {
                path: 'server-error',
                loadChildren: () => import('@frontend/vanilla/features/server-error').then((m) => m.ROUTES),
            },
            {
                path: '',
                canActivate: [activateHostProductGuard],
                children: [...VANILLA_LAZY_ROUTES],
            },
            {
                //TODO: cleanup when all dependencies are handled
                path: 'mobileportal/:pathMatch',
                redirectTo: 'myaccount/:pathMatch',
                pathMatch: 'prefix',
            },
            {
                path: 'promo',
                loadChildren: () => import('../../promo/app/src/promo.routes').then((t) => t.PROMO_ROUTES),
                canMatch: [clientConfigAppGuard],
                data: { product: ClientConfigProductName.PROMO },
            },
            {
                path: 'myaccount',
                loadChildren: () => import('../../myaccount/app/src/my-account.routes').then((t) => t.MY_ACCOUNT_ROUTES),
                canMatch: [clientConfigAppGuard],
                data: { product: ClientConfigProductName.PORTAL },
            },
            {
                path: 'virtualsports',
                loadChildren: () => import('../../virtualsports-app/src/virtual-sports.routes').then((t) => t.VIRTUAL_SPORTS_ROUTES),
                canActivate: [clientConfigAppGuard],
                data: { product: ClientConfigProductName.VIRTUALSPORTS },
            },
            {
                path: 'lotto',
                loadChildren: () => import('../../lottery-app/src/lottery.routes').then((t) => t.LottoRoutesModule),
                canMatch: [(route: ActivatedRouteSnapshot) => clientConfigAppGuard(route)],
                data: { product: ClientConfigProductName.LOTTO },
            },
            {
                path: 'horseracing',
                loadChildren: () => import('@frontend/horseracing').then((t) => t.HorseracingRoutesModule),
                canActivate: [(route: ActivatedRouteSnapshot) => clientConfigAppGuard(route)],
                data: { product: ClientConfigProductName.HORSERACING },
            },
            {
                path: 'bingo',
                loadChildren: () => import('../../bingo/app/src/bingo.routes').then((t) => t.BINGO_ROUTES),
                canActivate: [clientConfigAppGuard],
                data: { product: ClientConfigProductName.BINGO },
            },
            {
                path: 'mokabingo',
                loadChildren: () => import('../../mokabingo-app/src/mokabingo.routes').then((t) => t.MOKABINGO_ROUTES),
                canActivate: [clientConfigAppGuard],
                data: { product: ClientConfigProductName.MOKABINGO },
            },
            {
                path: 'games',
                loadChildren: () => import('../../casino/app/src/casino.routes').then((t) => t.CasinoRoutesModule),
                canMatch: [
                    (route: ActivatedRouteSnapshot) =>
                        matchAppVersionProductLoadGuard(ClientConfigProductName.CASINO, SamePathAppVersion.GAMES, route),
                ],
                data: { product: ClientConfigProductName.CASINO },
            },
            {
                path: 'casino',
                loadChildren: () => import('../../casino/app/src/casino.routes').then((t) => t.CasinoRoutesModule),
                canMatch: [
                    (route: ActivatedRouteSnapshot) =>
                        matchAppVersionProductLoadGuard(ClientConfigProductName.CASINO, SamePathAppVersion.CASINO, route),
                ],
                data: { product: ClientConfigProductName.CASINO },
            },
            {
                // For .de labels
                path: 'slots',
                loadChildren: () => import('../../casino/app/src/casino.routes').then((t) => t.CasinoRoutesModule),
                canMatch: [
                    (route: ActivatedRouteSnapshot) =>
                        matchAppVersionProductLoadGuard(ClientConfigProductName.CASINO, SamePathAppVersion.SLOTS, route),
                ],
                data: { product: ClientConfigProductName.CASINO },
            },
            {
                // for sportingbet.co.za labels
                path: 'livegames',
                loadChildren: () => import('../../casino/app/src/casino.routes').then((t) => t.CasinoRoutesModule),
                canMatch: [
                    (route: ActivatedRouteSnapshot) =>
                        matchAppVersionProductLoadGuard(ClientConfigProductName.CASINO, SamePathAppVersion.LIVEGAMES, route),
                ],
                data: { product: ClientConfigProductName.CASINO },
            },
            {
                path: 'sports',
                loadChildren: () => import('../../sports/web/app/src/sports-web-host-app.routes').then((t) => t.SportsWebIntegrationModule),
                canMatch: [
                    (route: ActivatedRouteSnapshot) =>
                        matchAppVersionProductLoadGuard(ClientConfigProductName.SPORTS, SamePathAppVersion.SPORTS_WEB_APP, route),
                ],
                data: { product: ClientConfigProductName.SPORTS },
            },
            {
                path: 'sports',
                children: [...SPORTS_BETSTATION_ROUTES],
                canMatch: [() => hostAppMatchAppVersionGuard(ClientConfigProductName.SPORTS, SamePathAppVersion.SPORTS_BETSTATION_APP)],
                canActivate: [clientConfigAppGuard],
                data: { product: ClientConfigProductName.SPORTS },
            },
            {
                path: 'sports',
                children: [...CORAL_DESKTOP_ROUTES],
                canMatch: [() => hostAppMatchAppVersionGuard(ClientConfigProductName.SPORTS, SamePathAppVersion.CORAL_DESKTOP_APP)],
                canActivate: [clientConfigAppGuard],
                data: { product: ClientConfigProductName.SPORTS },
            },
            {
                path: 'sports',
                children: [...CORAL_MOBILE_ROUTES],
                canMatch: [() => hostAppMatchAppVersionGuard(ClientConfigProductName.SPORTS, SamePathAppVersion.CORAL_MOBILE_APP)],
                canActivate: [clientConfigAppGuard],
                data: { product: ClientConfigProductName.SPORTS },
            },
            {
                path: 'sports',
                children: [...LADBROKES_DESKTOP_ROUTES],
                canMatch: [() => hostAppMatchAppVersionGuard(ClientConfigProductName.SPORTS, SamePathAppVersion.LADBROKES_DESKTOP_APP)],
                canActivate: [clientConfigAppGuard],
                data: { product: ClientConfigProductName.SPORTS },
            },
            {
                path: 'sports',
                children: [...LADBROKES_MOBILE_ROUTES],
                canMatch: [() => hostAppMatchAppVersionGuard(ClientConfigProductName.SPORTS, SamePathAppVersion.LADBROKES_MOBILE_APP)],
                canActivate: [clientConfigAppGuard],
                data: { product: ClientConfigProductName.SPORTS },
            },
            {
                path: 'poker',
                loadChildren: () => import('../../poker/app/src/poker.routes').then((t) => t.PokerRoutesModule),
                canActivate: [clientConfigAppGuard],
                data: { product: ClientConfigProductName.POKER },
            },
            {
                path: 'engage',
                loadChildren: () => import('../../engagement/app/src/engagement.routes').then((t) => t.ENGAGEMENT_ROUTES),
                canActivate: [clientConfigAppGuard],
                data: { product: ClientConfigProductName.ENGAGE },
            },
            {
                path: 'payments',
                loadChildren: () => import('../../payments/cashier-app/src/app/payments.routes').then((t) => t.PAYMENTS_ROUTES),
                canMatch: [clientConfigAppGuard],
                data: { product: ClientConfigProductName.PAYMENTS },
            },
        ],
        canActivateChild: [productStyleBundleGuard],
    },
    ...VANILLA_SECONDARY_ROUTES,
    {
        path: '**',
        loadChildren: () => import('@frontend/vanilla/features/not-found').then((m) => m.ROUTES),
    },
];
